import type {
  MemberRank,
  BookingHistory,
  CreditCardOnly,
  ServiceCouponGenre,
  SpecialCode,
} from './constants';

export type CouponTargetItem = {
  pi: string;
  rc: string;
  pr: number;
  mbpr: number;
  st: number;
  cf: number;
  cfpn?: number;
  ptbd: number;
  ptrt: number;
  ca: string;
};

export type CouponRequestParams = {
  hn: string;
  t: CouponTargetItem[];
  ci: string;
  co: string;
  ad: number;
  ch: number;
  cb: string;
  mf: string;
  hs?: number;
  rzcookie?: string;
  referer?: string;
  easy_id?: string;
  ma?: string;
  rs?: string;
  excl?: string[];
  cty?: number[];
  srv?: string;
  app?: string;
  scf?: string;
  gf?: string;
};

export type CouponParams = {
  requestString: string;
};

/**
 * Coupon data from API
 * Enhanced with specific type annotations for better type safety
 */
export type Coupon = {
  /** Discount percentage */
  dp?: number;
  /** Discount flag */
  df?: number;
  /** Discount type */
  dt?: number;
  /** Coupon code */
  cc?: string;
  /** Group key */
  gk?: string;
  /** Auto apply coupon */
  ac?: boolean;
  /** End date in format "YYYY-MM-DD HH:mm:ss" */
  ed?: string;
  /** Coupon name/description */
  dc?: string;
  /** Coupon type */
  ct?: string;
  /** Stack option */
  so?: boolean;
  /** Discount rate */
  dr?: number;
  /** Discount type flag */
  dtf?: number;
  /** Service coupon genre */
  sg?: ServiceCouponGenre;
  /** Discount amount in yen */
  da?: number;
  /** Usage limit */
  ulm?: number;
  /** Member rank */
  mr?: MemberRank;
  /** Booking history */
  bh?: BookingHistory;
  /** Excluded plan codes */
  epc?: number[];
  /** Credit card only flag from backend */
  cco?: CreditCardOnly;
  /** Special code */
  sc?: SpecialCode;
};

export type CouponResultItem = {
  hn: number;
  pi: number;
  rc: string;
  pr: number;
  tdp: number;
  dp: number;
  prp: number;
  prpt: number;
  rs: string;
  taxType: string;
  selectedPriceType?: number;
  membershipDiscountedPrice?: number;
  ptad: number;
  coupons: Coupon[];
};

export type CampaignInfo = {
  hn: number;
  pi: number;
  rc: string;
  sbpr: number;
  supr: number;
  suprl: number;
  suprc: number;
  pta: number;
  ptac: number;
  ptal: number;
  rs: string;
  isbp: boolean;
  crpsp: boolean;
  bp: number;
  bpl: number;
  bpc: number;
};

export type CouponResponse = {
  status: string;
  results: CouponResultItem[];
  campaignInfos?: CampaignInfo[];
};

export type BonusProgramResponse = {
  /** Bonus program level 0~4 */
  bonusLevel: number;
  /** Boosted level 0~4 */
  boostedLevel: number;
  /** Number of stays */
  bookingCount: number;
  /** Point rate for target hotel (0, 0.5, 1, 1.5, 2.5) */
  bonusPoint: number;
  /** Boosted point rate for target hotel; null if no boost */
  boostedPoint?: number | null;
  /** Boosted note text; null or empty string when no boost */
  boostedNote?: string | null;
  /** Boosted period start date (YYYY-MM-DD) */
  boostedStartDate?: string;
  /** Boosted period end date (YYYY-MM-DD); null if not available */
  boostedEndDate?: string | null;
  /** URL for the "View Details" button */
  link: string;
  /** true = boosted, false = regular */
  isBoosted: boolean;
  /** Level change from previous month: 0=no change, 1=level up, 2=level down */
  levelUpDown: number;
  /** Target month in YYYYMM format */
  baseMonth: string;
  /** BP count for past year, semicolon-separated 12 values */
  pastYearCount: string;
  /** BP count for next year, semicolon-separated 12 values */
  nextYearCount: string;
  /** Minimum point rate when paying on-site */
  bonusMinPointRate: number;
  /** Maximum point rate when paying by card */
  bonusMaxPointRate: number;
  /** Boosted minimum point rate when paying on-site */
  boostedMinPointRate: number;
  /** Boosted maximum point rate when paying by card */
  boostedMaxPointRate: number;
  /** Minimum BP discount percentage */
  minDiscountPercentage?: number;
  /** Maximum BP discount percentage */
  maxDiscountPercentage?: number;
  /** Minimum BP discount percentage for search (local payment) */
  minDiscountPercentageForSearchLocal?: number;
  /** Minimum BP discount percentage for search (card payment) */
  minDiscountPercentageForSearchCard?: number;
  /** true if BonusProgramLevel uses old range (0~4), false if new range (1~5) */
  bplRangeOld?: boolean;
};

export type ThunkErrorPayload = {
  message: string;
  name?: string;
  status?: number;
};

export type CouponEntry = {
  data: CouponResponse | null;
  isFetching: boolean;
  error: any;
};

export type BonusProgramEntry = {
  data: BonusProgramResponse | null;
  isFetching: boolean;
  error: ThunkErrorPayload | null;
};

export type DomCouponState = {
  coupons: {
    [providerId: number]: CouponEntry;
  };
  bonusProgram?: BonusProgramEntry;
};

export enum DomPaymentTypes {
  LOCAL = 'LOCAL',
  CREDIT_CARD_LOCAL = 'CREDIT_CARD_LOCAL',
  CREDIT_CARD = 'CREDIT_CARD',
}
